import React, { createContext, useState, useEffect, useContext } from 'react';

const UserContext = createContext();

// Auth token exchange URL
const AUTH_EXCHANGE_URL = process.env.REACT_APP_LOOPAI_BACKEND_URL || 'https://app.loopai.in';

// Fallback API URL for non-authenticated calls
const FALLBACK_API_URL = 'https://app.loopai.in';

/**
 * SECURE TOKEN-BASED AUTHENTICATION
 * 
 * Flow:
 * 1. Dashboard redirects with auth_token in URL (token is short-lived, single-use)
 * 2. App exchanges token via POST to /api/integration/exchange-token
 * 3. Credentials stored in localStorage
 * 4. Token is cleared from URL immediately
 */

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [apiKey, setApiKey] = useState(null);
  const [apiUrl, setApiUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      // STEP 1: Check for auth_token in URL (from dashboard redirect)
      const params = new URLSearchParams(window.location.search);
      const authToken = params.get('auth_token');
      
      if (authToken) {
        console.log('🔐 Auth token detected, exchanging for credentials...');
        
        // STEP 2: Exchange token for actual credentials via POST
        const tokenResponse = await fetch(
          `${AUTH_EXCHANGE_URL}/api/integration/exchange-token`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: authToken })
          }
        );
        
        if (tokenResponse.ok) {
          const data = await tokenResponse.json();
          console.log('✅ Token exchanged successfully:', data.user?.email);
          
          const { user: userData, api_key, api_url, credits } = data;
          
          // Store in localStorage for persistence
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('api_key', api_key);
          localStorage.setItem('api_url', api_url || FALLBACK_API_URL);
          localStorage.setItem('credits', (credits || 0).toString());
          
          setUser({
            ...userData,
            credits: credits || userData?.credits || 0,
            isAuthenticated: true
          });
          setApiKey(api_key);
          setApiUrl(api_url || FALLBACK_API_URL);
          
          // Clean up URL (remove auth_token param)
          params.delete('auth_token');
          const cleanUrl = params.toString()
            ? `${window.location.pathname}?${params.toString()}`
            : window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
          console.log('🔒 Token cleared from URL');
          
          setLoading(false);
          return;
        } else {
          const errorData = await tokenResponse.json().catch(() => ({}));
          console.error('❌ Token exchange failed:', errorData.detail || tokenResponse.status);
        }
      }
      
      // STEP 3: Try stored credentials from localStorage
      const storedUser = localStorage.getItem('user');
      const storedApiKey = localStorage.getItem('api_key');
      const storedApiUrl = localStorage.getItem('api_url');
      const storedCredits = localStorage.getItem('credits');
      
      if (storedUser && storedApiKey) {
        console.log('🔑 Using stored credentials from localStorage');
        const parsedUser = JSON.parse(storedUser);
        
        setUser({
          ...parsedUser,
          credits: storedCredits ? parseInt(storedCredits, 10) : 0,
          isAuthenticated: true
        });
        setApiKey(storedApiKey);
        setApiUrl(storedApiUrl || FALLBACK_API_URL);
        setLoading(false);
        return;
      }
      
      // STEP 4: No authentication available - guest mode
      console.log('⚠️ No authentication available - guest mode');
      setUser({
        email: 'guest@loopai.com',
        id: 'guest',
        name: 'Guest User',
        credits: 0,
        isAuthenticated: false
      });
      setApiUrl(FALLBACK_API_URL);
      
    } catch (error) {
      console.error('❌ Authentication error:', error);
      
      // Fall back to stored credentials on network error
      const storedUser = localStorage.getItem('user');
      const storedApiKey = localStorage.getItem('api_key');
      const storedApiUrl = localStorage.getItem('api_url');
      const storedCredits = localStorage.getItem('credits');
      
      if (storedUser && storedApiKey) {
        const parsedUser = JSON.parse(storedUser);
        setApiKey(storedApiKey);
        setApiUrl(storedApiUrl || FALLBACK_API_URL);
        setUser({
          ...parsedUser,
          credits: storedCredits ? parseInt(storedCredits, 10) : 0,
          isAuthenticated: true
        });
      } else {
        setUser({
          email: 'guest@loopai.com',
          id: 'guest',
          name: 'Guest User',
          credits: 0,
          isAuthenticated: false
        });
        setApiUrl(FALLBACK_API_URL);
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshCredits = async () => {
    try {
      const storedApiKey = localStorage.getItem('api_key');
      const storedApiUrl = localStorage.getItem('api_url') || FALLBACK_API_URL;
      const storedUser = localStorage.getItem('user');
      const userEmail = storedUser ? JSON.parse(storedUser).email : null;
      
      const headers = { 'Content-Type': 'application/json' };
      if (storedApiKey) headers['X-API-Key'] = storedApiKey;
      if (userEmail) headers['X-User-Email'] = userEmail;
      
      const response = await fetch(`${storedApiUrl}/api/credits`, {
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(prev => ({ ...prev, credits: data.credits }));
        localStorage.setItem('credits', data.credits.toString());
      }
    } catch (error) {
      console.error('Error refreshing credits:', error);
    }
  };

  const updateCredits = (newCredits) => {
    setUser(prev => ({
      ...prev,
      credits: newCredits
    }));
    localStorage.setItem('credits', newCredits.toString());
  };
  
  const logout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('api_key');
    localStorage.removeItem('api_url');
    localStorage.removeItem('credits');
    setUser(null);
    setApiKey(null);
    setApiUrl(null);
    console.log('🔓 User logged out, credentials cleared');
  };

  const value = {
    user,
    apiKey,
    apiUrl,
    loading,
    refreshCredits,
    updateCredits,
    logout,
    isAuthenticated: user?.isAuthenticated || false
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error('useUser must be used within UserProvider');
  return context;
};
